// Citations on `:documentation{id=… version-id=… page=… quote=…}` and
// `:documentation{id=… time=…}` chips (BE guide §5). The attributes are written
// by the model and are NEVER trusted on their own: a page or moment is shown
// only when the message's `citations` list verifies it (a place the model was
// actually shown while answering).

import { AiCitation, AiCitationPassage } from 'graphql-common';
import { PdfPageHighlight } from '@lib/components/PDFViewer/types';
import { foldText } from '@lib/components/PDFViewer/textHighlight';

// Mirrors the BE's Ai::CitationLocator::MAX_LOCATIONS (BE guide §5) — places
// beyond this many on one chip (pages or times) are never verified, so they're
// never worth reading. Keep in sync with the BE.
export const MAX_CITATION_PAGES = 5;

// Derived from the generated type so it can't drift from the schema. Fields
// added after page citations are optional so a partial entry still matches.
export type AiCitationRef = Pick<
  AiCitation,
  'documentationId' | 'documentationVersionId' | 'page'
> &
  Partial<
    Pick<AiCitation, 'pageAspectRatio' | 'startSeconds' | 'endSeconds'> & {
      passages: Pick<AiCitationPassage, 'text' | 'quote' | 'regions'>[];
    }
  >;

export type VerifiedCitationPages = {
  // The version whose file the pages belong to — open THIS version's file.
  versionId: string;
  // Verified pages, ascending and unique.
  pages: number[];
  // Passages to highlight when the chip opens, by page (ascending); pages
  // with nothing to highlight are left out.
  highlights: PdfPageHighlight[];
};

export type CitationMoment = {
  startSeconds: number;
  // Null when the video's length is unknown — mark only the start.
  endSeconds: number | null;
};

export type VerifiedCitationMoments = {
  // The version whose video the moments belong to.
  versionId: string;
  // Verified moments, by start, unique.
  moments: CitationMoment[];
};

export type PageRange = { from: number; to: number };

const WHOLE_NUMBER = /^\d+$/;

// A chip attribute's comma-separated tokens, unquoted, with en/em dashes
// normalized to "-".
const splitAttributeTokens = (value: string) =>
  value.replace(/["']/g, '').replace(/[–—]/g, '-').split(',');

// Deduplicated in order of appearance, at most MAX_CITATION_PAGES.
const capUnique = (values: number[]) =>
  [...new Set(values)].slice(0, MAX_CITATION_PAGES);

// Parses a chip's `page` attribute like the BE locator (BE guide §5): "4",
// "4,9", "4-6" (a range; en/em dashes count as one), quoted or not — whole
// numbers in order of appearance, deduplicated, at most MAX_CITATION_PAGES.
// A range's start is clamped to 1; a bare 0, a reversed range and other
// malformed tokens are dropped, and callers log when a non-empty value yields
// nothing. Accepting more spellings than the BE is safe: a page still shows
// only once `citations` verifies it.
export function parseCitationPages(value?: string | null): number[] {
  if (!value) return [];
  const pages = splitAttributeTokens(value).flatMap((token) => {
    const trimmed = token.trim();
    const dash = trimmed.indexOf('-');
    const first = (dash === -1 ? trimmed : trimmed.slice(0, dash)).trim();
    const last = dash === -1 ? undefined : trimmed.slice(dash + 1).trim();
    if (
      !WHOLE_NUMBER.test(first) ||
      (last !== undefined && !WHOLE_NUMBER.test(last))
    ) {
      return [];
    }
    // Pages count from 1: clamp before the cap so "0-9" still yields 5 pages.
    const from = Math.max(Number(first), 1);
    const to = last !== undefined ? Number(last) : Number(first);
    const range: number[] = [];
    for (
      let page = from;
      page <= to && range.length < MAX_CITATION_PAGES;
      page += 1
    ) {
      range.push(page);
    }
    return range;
  });
  return capUnique(pages);
}

// `mm:ss` or `h:mm:ss` (BE Ai::Indexing::VideoStamps).
const STAMP = /^\d+(?::\d{1,2}){1,2}$/;

// Parses a chip's `time` attribute like the BE locator: "03:00",
// "03:00,03:40", quoted or not; a range token ("01:25-02:10") counts by its
// start, which is all matching needs. Seconds in order of appearance,
// deduplicated, at most MAX_CITATION_PAGES; unreadable tokens are dropped.
export function parseCitationTimes(value?: string | null): number[] {
  if (!value) return [];
  const times = splitAttributeTokens(value).flatMap((token) => {
    const stamp = token.split('-')[0].trim();
    if (!STAMP.test(stamp)) return [];
    return [
      stamp.split(':').reduce((total, part) => total * 60 + Number(part), 0),
    ];
  });
  return capUnique(times);
}

const sameId = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

// The server stores the chip's quote on the passage as saved, so they should
// be equal; compare them folded the way the text-layer search folds text
// (case, spacing, quote and dash styles, `...` vs `…`) so a small drift in
// spelling still matches.
export const normalizeQuote = (quote: string) => foldText(quote);

// Entries citing the chip's document — and its pinned version, when it has
// one — that pass `isMatch`. The chip resolves to the version of the first
// entry matching its first place (in the chip's order); when several versions
// match that place, the first in `citations` order wins (the BE only verifies
// such a chip when exactly one shown version had it).
function matchVersionEntries<Place>(
  chip: { id: string; versionId?: string; places: Place[] },
  citations: AiCitationRef[] | undefined,
  isMatch: (citation: AiCitationRef, place: Place) => boolean,
): { versionId: string; entries: AiCitationRef[] } | null {
  if (!chip.places.length || !citations?.length) return null;
  const ofChip = citations.filter(
    (citation) =>
      sameId(citation.documentationId, chip.id) &&
      (!chip.versionId ||
        sameId(citation.documentationVersionId, chip.versionId)),
  );
  const first = chip.places
    .map((place) => ofChip.find((citation) => isMatch(citation, place)))
    .find(Boolean);
  if (!first) return null;
  const versionId = first.documentationVersionId;
  return {
    versionId,
    entries: ofChip.filter(
      (citation) =>
        sameId(citation.documentationVersionId, versionId) &&
        chip.places.some((place) => isMatch(citation, place)),
    ),
  };
}

// Passages to highlight, grouped by page: those of `entries` whose quote
// folds to `wanted` (all of them when `wanted` is null).
function groupPassagesByPage(
  entries: AiCitationRef[],
  wanted: string | null,
): PdfPageHighlight[] {
  const byPage = new Map<number, PdfPageHighlight>();
  entries.forEach((citation) => {
    const passages = (citation.passages ?? []).filter(
      (passage) => !wanted || normalizeQuote(passage.quote) === wanted,
    );
    if (!passages.length) return;
    const page = citation.page as number;
    const highlight = byPage.get(page) ?? {
      page,
      aspectRatio: citation.pageAspectRatio,
      passages: [],
    };
    passages.forEach(({ text, regions }) => {
      if (!highlight.passages.some((existing) => existing.text === text)) {
        highlight.passages.push({ text, regions });
      }
    });
    byPage.set(page, highlight);
  });
  return [...byPage.values()].sort((a, b) => a.page - b.page);
}

// Passages to highlight for a verified page chip, grouped by page. A chip
// with a `quote` highlights its passage in EVERY entry of the version — a
// passage running across a page break comes as one entry per page, and the
// chip names only one of them. A chip without one — or whose quote matches no
// stored passage — highlights all passages of its matched entries.
function getPageHighlights(
  quote: string | undefined,
  versionId: string,
  matched: AiCitationRef[],
  citations: AiCitationRef[],
): PdfPageHighlight[] {
  const wanted = quote ? normalizeQuote(quote) : null;
  if (wanted) {
    const quoted = groupPassagesByPage(
      citations.filter(
        (citation) =>
          typeof citation.page === 'number' &&
          sameId(citation.documentationVersionId, versionId),
      ),
      wanted,
    );
    if (quoted.length) return quoted;
  }
  return groupPassagesByPage(matched, null);
}

// Matches a page chip to the message's verified citations (see
// matchVersionEntries). Returns null when nothing verifies — the chip then
// behaves as a plain document reference.
export function getVerifiedCitationPages(
  chip: { id: string; versionId?: string; pages: number[]; quote?: string },
  citations: AiCitationRef[] | undefined,
): VerifiedCitationPages | null {
  const match = matchVersionEntries(
    { id: chip.id, versionId: chip.versionId, places: chip.pages },
    citations,
    (citation, page) => citation.page === page,
  );
  if (!match) return null;
  const { versionId, entries } = match;
  const pages = entries.map((citation) => citation.page as number);
  return {
    versionId,
    pages: [...new Set(pages)].sort((a, b) => a - b),
    highlights: getPageHighlights(
      chip.quote,
      versionId,
      entries,
      citations as AiCitationRef[],
    ),
  };
}

// Whether a video entry covers the chip time `t`: `start ≤ t < end`, or
// `start = t` for an entry whose end is unknown.
const coversTime = (citation: AiCitationRef, t: number) => {
  const { page, startSeconds: start, endSeconds: end } = citation;
  if (page != null || typeof start !== 'number') return false;
  return typeof end === 'number' ? start <= t && t < end : start === t;
};

// Matches a video chip's `time` stamps to the message's verified moments.
// Returns null when nothing verifies — the chip then just opens the video.
export function getVerifiedCitationMoments(
  chip: { id: string; versionId?: string; times: number[] },
  citations: AiCitationRef[] | undefined,
): VerifiedCitationMoments | null {
  const match = matchVersionEntries(
    { id: chip.id, versionId: chip.versionId, places: chip.times },
    citations,
    coversTime,
  );
  if (!match) return null;
  const moments = new Map<number, CitationMoment>();
  match.entries.forEach(({ startSeconds, endSeconds }) => {
    const start = startSeconds as number;
    const known = moments.get(start);
    const end = endSeconds ?? null;
    if (!known || (end !== null && (known.endSeconds ?? -1) < end)) {
      moments.set(start, { startSeconds: start, endSeconds: end });
    }
  });
  return {
    versionId: match.versionId,
    moments: [...moments.values()].sort(
      (a, b) => a.startSeconds - b.startSeconds,
    ),
  };
}

export type DocRefCitation = {
  // The version the chip opens: the pinned `version-id`, else the version the
  // verified pages or moments belong to, else undefined (the document's
  // current one).
  versionId?: string;
  citation: VerifiedCitationPages | null;
  moments: VerifiedCitationMoments | null;
};

// Resolves an `ai-doc-ref` element's attributes against the message's
// citations. A chip without `version-id` that verifies resolves to the cited
// version — the file the page numbers or times belong to.
export function resolveDocRefCitation(
  chip: {
    id: string;
    versionId?: string | null;
    pages: number[];
    quote?: string | null;
    times?: number[];
  },
  citations: AiCitationRef[] | undefined,
): DocRefCitation {
  const pinnedVersionId = chip.versionId || undefined;
  const citation = getVerifiedCitationPages(
    {
      id: chip.id,
      versionId: pinnedVersionId,
      pages: chip.pages,
      quote: chip.quote || undefined,
    },
    citations,
  );
  const moments = getVerifiedCitationMoments(
    { id: chip.id, versionId: pinnedVersionId, times: chip.times ?? [] },
    citations,
  );
  return {
    versionId: pinnedVersionId || citation?.versionId || moments?.versionId,
    citation,
    moments,
  };
}

// A verified citation, only for the version a chip resolved to: page numbers
// and times only mean something in the file they were read from, so any other
// version (e.g. a pinned version that failed to load or errored and fell back
// to the current one) gets none.
export function getCitationForVersion<T extends { versionId: string }>(
  citation: T | null | undefined,
  resolvedVersionId?: string | null,
): T | undefined {
  if (!citation || !sameId(citation.versionId, resolvedVersionId)) {
    return undefined;
  }
  return citation;
}

// Collapses pages into consecutive runs: [4, 5, 9] -> 4–5, 9. The input must be
// ascending and unique (as `VerifiedCitationPages.pages` is).
export function groupPageRanges(pages: number[]): PageRange[] {
  return pages.reduce<PageRange[]>((ranges, page) => {
    const last = ranges[ranges.length - 1];
    if (last && page === last.to + 1) {
      last.to = page;
    } else {
      ranges.push({ from: page, to: page });
    }
    return ranges;
  }, []);
}
